import { z } from 'zod';
import { EventDay, EventFlag, EventItemKey, EventNoQuery, EventRowId, EventText } from './eventPlanningFields';

// Information packages, the communications plan and per-session feedback of an in-person event
// (/api/events/:id/info, /api/events/:id/feedback). Packages and plan items are sent back as the GET
// returns them, so they take the view's extra fields; the service keeps the fields it knows.
// The lists mirror the info rules (the tests compare them).

export const EventPackageMode = z.enum(['in_person', 'virtual']);
export type EventPackageMode = z.infer<typeof EventPackageMode>;

export const EventCommsType = z.enum([
  'save_the_date',
  'rsvp',
  'confirmation',
  'invitation',
  'virtual_invite',
  'announcement',
  'ambassador',
  'calendar',
  'notes_for_attendees',
  'reminder',
  'evaluation',
  'custom',
]);
export type EventCommsType = z.infer<typeof EventCommsType>;

export const EventCommsCopyStatus = z.enum(['none', 'drafted', 'approved']);
export type EventCommsCopyStatus = z.infer<typeof EventCommsCopyStatus>;

export const EVENT_MAX_PACKAGES = 12;
export const EVENT_MAX_COMMS = 60;

// ---------------------------------------------------------------- params and queries

export const EventInfoParams = z.strictObject({ id: EventRowId });
/** Unknown package keys answer 404 from the service. */
export const EventInfoPackageParams = z.strictObject({ id: EventRowId, key: EventItemKey });
export const EventInfoCommsItemParams = z.strictObject({ id: EventRowId, itemId: EventItemKey });
export const EventInfoNoQuery = EventNoQuery;

/** GET /api/events/:id/info/packages/:key?lang=en|fr (the service refuses other languages). */
export const EventInfoPackageQuery = z.strictObject({ lang: z.string().max(10).optional() });
export type EventInfoPackageQuery = z.input<typeof EventInfoPackageQuery>;

// ---------------------------------------------------------------- requests

export const EventInfoPackageInput = z.looseObject({
  key: z.string().max(60).nullish(),
  name: EventText(),
  nameFr: EventText(),
  mode: z.enum(EventPackageMode.options, { error: 'Mode is in_person or virtual' }).optional(),
  audienceKeys: z.array(z.string().max(60)).max(50).nullish(),
  sections: z.record(z.string(), z.unknown()).nullish(),
  notesEn: EventText(),
  notesFr: EventText(),
  publishedUrl: EventText(),
});
export type EventInfoPackageInput = z.input<typeof EventInfoPackageInput>;

export const EventInfoCommonText = z
  .strictObject({
    arrivalEn: EventText(),
    arrivalFr: EventText(),
    parkingEn: EventText(),
    parkingFr: EventText(),
    accessibilityEn: EventText(),
    accessibilityFr: EventText(),
  })
  .partial();
export type EventInfoCommonText = z.input<typeof EventInfoCommonText>;

/** PUT /api/events/:id/info — the whole package list as sent (keys kept, new ones made from the name), and the shared texts. */
export const EventInfoRequest = z
  .strictObject({
    packages: z
      .array(EventInfoPackageInput.nullable())
      .max(EVENT_MAX_PACKAGES, `At most ${EVENT_MAX_PACKAGES} packages`),
    common: EventInfoCommonText.nullish(),
  })
  .partial();
export type EventInfoRequest = z.input<typeof EventInfoRequest>;

/** POST /api/events/:id/info/packages/:key/published */
export const EventInfoPublishedRequest = z.strictObject({ url: EventText(), undo: EventFlag });
export type EventInfoPublishedRequest = z.input<typeof EventInfoPublishedRequest>;

export const EventCommsItemInput = z.looseObject({
  id: z.string().max(200).nullish(),
  audienceKey: z.string().max(60).nullish(),
  type: z
    .enum(EventCommsType.options, { error: `Message type is one of ${EventCommsType.options.join(', ')}` })
    .optional(),
  label: EventText(),
  offsetDays: z.union([z.number(), z.string().max(10), z.null()]).optional(),
  sendOn: EventDay,
  copyStatus: z.enum(EventCommsCopyStatus.options, { error: 'Copy is none, drafted or approved' }).optional(),
  copyLink: EventText(),
  values: z.record(z.string(), z.string().nullish()).nullish(),
});
export type EventCommsItemInput = z.input<typeof EventCommsItemInput>;

/** PUT /api/events/:id/info/comms — the whole plan. */
export const EventCommsPlanRequest = z.strictObject({
  items: z
    .array(EventCommsItemInput.nullable())
    .max(EVENT_MAX_COMMS, `At most ${EVENT_MAX_COMMS} messages in the plan`)
    .optional(),
});
export type EventCommsPlanRequest = z.input<typeof EventCommsPlanRequest>;

/** POST /api/events/:id/info/comms/:itemId/sent */
export const EventCommsSentRequest = z.strictObject({ undo: EventFlag });
export type EventCommsSentRequest = z.input<typeof EventCommsSentRequest>;

/** PUT /api/events/:id/feedback/mapping — answer → session id and feedback form → session id; empty clears. */
export const EventFeedbackMappingRequest = z
  .strictObject({
    answers: z.record(z.string().max(1000), z.string().max(200).nullish()),
    editions: z.record(z.string().max(200), z.string().max(200).nullish()),
  })
  .partial();
export type EventFeedbackMappingRequest = z.input<typeof EventFeedbackMappingRequest>;

/** POST /api/events/:id/feedback/zoom-poll — a session's Zoom poll report (CSV text); dry run unless confirm is true. */
export const EventSessionPollRequest = z.strictObject({
  sessionId: z.string().max(200).nullish(),
  fileName: z.string().max(500).nullish(),
  content: z.string().optional(),
  confirm: z.boolean().optional(),
});
export type EventSessionPollRequest = z.input<typeof EventSessionPollRequest>;
